import { request } from '@/lib/api'

/**
 * Мессенджеры (тикет #125): диалоги Telegram и WhatsApp в карточке клиента, ответ в тот же чат,
 * очередь разбора неопознанных собеседников и настройки каналов.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export type MessengerChannel = 'telegram' | 'whatsapp'

/** received — входящее; pending/sending — в пути; sent; failed — повтор позже; rejected — отказ. */
export type MessageStatus = 'received' | 'pending' | 'sending' | 'sent' | 'failed' | 'rejected'

export interface Conversation {
  id: string
  channel: MessengerChannel
  title: string
  username?: string | null
  phone?: string | null
  status: 'matched' | 'unmatched' | 'dismissed'
  lastInboundAt?: string | null
  lastMessageAt?: string | null
  /** Только WhatsApp: можно ли сейчас писать свободным текстом (24 часа с сообщения клиента). */
  windowOpen?: boolean | null
  windowClosesAt?: string | null
  /** Канал подключён; без этого ответ получит отказ словами. */
  usable: boolean
}

export interface SentMessage {
  id: string
  conversationId: string
  direction: 'inbound' | 'outbound'
  body: string
  status: MessageStatus
  reason?: string | null
  attempts: number
  retryAfter?: string | null
  sentAt?: string | null
}

export interface QueuedMessage {
  id: string
  direction: 'inbound' | 'outbound'
  body: string
  status: MessageStatus
  sentAt?: string | null
}

export interface MessengerQueueItem {
  id: string
  channel: MessengerChannel
  title: string
  username?: string | null
  phone?: string | null
  /** Почему диалог не привязался сам: номер не назван, найден у нескольких… */
  reason?: string | null
  lastMessageAt?: string | null
  /** Подсказка по @username из карточки контакта — только подсказка. */
  suggestedContactId?: string | null
  suggestedContactName?: string | null
  messages?: QueuedMessage[]
}

export type MessengerAssignTarget = 'contact' | 'counterparty'

export interface MessengerSettings {
  channel: MessengerChannel
  enabled: boolean
  endpoint?: string | null
  /** Telegram — токен бота; WhatsApp — постоянный токен доступа. */
  tokenSet: boolean
  /** Telegram: secret_token вебхука. */
  secretSet: boolean
  /** WhatsApp: App secret для подписи X-Hub-Signature-256. */
  appSecretSet: boolean
  verifyTokenSet: boolean
  phoneNumberId?: string | null
  usable: boolean
  problem?: string | null
  webhookPath: string
}

export interface MessengerSettingsUpdate {
  enabled: boolean
  endpoint: string | null
  /** `undefined` — оставить сохранённое, пустая строка — стереть. */
  token?: string
  secret?: string
  appSecret?: string
  verifyToken?: string
  phoneNumberId?: string | null
}

export interface MessengerEvent {
  id: string
  channel: MessengerChannel
  signatureValid: boolean
  status: 'processed' | 'duplicate' | 'ignored' | 'rejected' | 'failed'
  messages: number
  detail?: string | null
  remoteAddress?: string | null
  receivedAt: string
}

export function fetchConversations(entityType: MessengerAssignTarget, entityId: string, signal?: AbortSignal): Promise<Conversation[]> {
  return request<{ conversations?: Conversation[] }>(
    `/messenger/conversations?entityType=${entityType}&entityId=${encodeURIComponent(entityId)}`,
    { signal },
  ).then((body) => body.conversations ?? [])
}

/** `draftTaskId` — черновик ИИ, с которого начат текст (#134); уходит текст, а не черновик. */
export function replyInChat(conversationId: string, text: string, idempotencyKey: string,
                            draftTaskId: string | null = null): Promise<SentMessage> {
  return request<SentMessage>(`/messenger/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { text, draftTaskId },
    idempotencyKey,
  })
}

export function fetchMessengerQueue(signal?: AbortSignal): Promise<MessengerQueueItem[]> {
  return request<{ items?: MessengerQueueItem[] }>('/messenger/inbox', { signal }).then((body) => body.items ?? [])
}

export function assignConversation(id: string, entityType: MessengerAssignTarget, entityId: string): Promise<void> {
  return request<void>(`/messenger/inbox/${id}/assign`, { method: 'POST', body: { entityType, entityId } })
}

export function ticketFromConversation(id: string, counterpartyId: string): Promise<{ id: string; number: string }> {
  return request<{ id: string; number: string }>(`/messenger/inbox/${id}/ticket`, {
    method: 'POST',
    body: { counterpartyId },
  })
}

export function dismissConversation(id: string): Promise<void> {
  return request<void>(`/messenger/inbox/${id}/dismiss`, { method: 'POST', body: {} })
}

export function fetchMessengerSettings(signal?: AbortSignal): Promise<Record<MessengerChannel, MessengerSettings>> {
  return request<Record<MessengerChannel, MessengerSettings>>('/admin/messengers', { signal })
}

export function updateMessengerSettings(channel: MessengerChannel, update: MessengerSettingsUpdate): Promise<MessengerSettings> {
  return request<MessengerSettings>(`/admin/messengers/${channel}`, { method: 'PUT', body: update })
}

export function registerTelegramWebhook(url: string): Promise<{ registered: boolean; url: string }> {
  return request<{ registered: boolean; url: string }>('/admin/messengers/telegram/webhook', {
    method: 'POST',
    body: { url },
  })
}

export function fetchMessengerEvents(signal?: AbortSignal): Promise<MessengerEvent[]> {
  return request<MessengerEvent[]>('/admin/messengers/events?limit=50', { signal })
}
